import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';

export type SkeletonType = 'text' | 'title' | 'avatar' | 'thumbnail' | 'button' | 'card' | 'table-row' | 'paragraph';
export type SkeletonAnimation = 'wave' | 'glow' | 'none';
export type SkeletonVariant = 'default' | 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'dark' | 'light';

/**
 * Skeleton Loading Component
 *
 * Displays placeholder content while data is loading.
 * Supports wave/glow animations and color variants.
 *
 * Usage:
 * ```html
 * <!-- Single line of text -->
 * <app-skeleton type="text"></app-skeleton>
 *
 * <!-- Title with wave animation -->
 * <app-skeleton type="title" [width]="60" animation="wave"></app-skeleton>
 *
 * <!-- Avatar with glow animation -->
 * <app-skeleton type="avatar" size="md" animation="glow"></app-skeleton>
 *
 * <!-- Card skeleton with primary color -->
 * <app-skeleton type="card" variant="primary"></app-skeleton>
 *
 * <!-- Table row -->
 * <app-skeleton type="table-row" [columns]="5"></app-skeleton>
 *
 * <!-- Paragraph (multiple lines with varying widths) -->
 * <app-skeleton type="paragraph" [lines]="4"></app-skeleton>
 *
 * <!-- With color variants -->
 * <app-skeleton type="text" variant="primary"></app-skeleton>
 * <app-skeleton type="text" variant="success"></app-skeleton>
 * ```
 */
@Component({
  selector: 'app-skeleton',
  standalone: false,
  templateUrl: './skeleton.component.html',
  styleUrl: './skeleton.component.scss'
})
export class SkeletonComponent implements OnInit, OnChanges {
  /** Type of skeleton to display */
  @Input() type: SkeletonType = 'text';

  /** Animation style: wave, glow, or none */
  @Input() animation: SkeletonAnimation = 'wave';

  /** Color variant */
  @Input() variant: SkeletonVariant = 'default';

  /** Width percentage (for text/title types) */
  @Input() width = 100;

  /** Number of lines (for text/paragraph type) */
  @Input() lines = 1;

  /** Size for avatar type */
  @Input() size: 'xs' | 'sm' | 'md' | 'lg' | 'xl' = 'md';

  /** Number of columns for table-row type */
  @Input() columns = 4;

  /** Height in pixels (for custom sizing) */
  @Input() height: number | null = null;

  /** @deprecated Use animation="none" instead */
  @Input() set animate(value: boolean) {
    if (!value) {
      this.animation = 'none';
    }
  }

  // Pre-computed widths to avoid change detection issues
  private _lineWidths: number[] = [];
  private _paragraphWidths: number[] = [];

  ngOnInit(): void {
    this.computeWidths();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['lines'] || changes['width'] || changes['type']) {
      this.computeWidths();
    }
  }

  private computeWidths(): void {
    // Pre-compute line widths for text type
    this._lineWidths = Array(this.lines).fill(0).map((_, index) => {
      if (this.lines === 1) return this.width;
      if (index === this.lines - 1) return Math.min(this.width, 50 + Math.random() * 30);
      return 75 + Math.random() * 25;
    });

    // Pre-compute paragraph widths
    this._paragraphWidths = Array(this.lines).fill(0).map((_, index) => {
      if (index === 0) return 100;
      if (index === this.lines - 1) return 40 + Math.random() * 30;
      return 85 + Math.random() * 15;
    });
  }

  get linesArray(): number[] {
    return Array(this.lines).fill(0).map((_, i) => i);
  }

  get columnsArray(): number[] {
    return Array(this.columns).fill(0).map((_, i) => i);
  }

  get animationClass(): string {
    if (this.animation === 'none') return '';
    return `skeleton-${this.animation}`;
  }

  get variantClass(): string {
    if (this.variant === 'default') return '';
    return `skeleton-${this.variant}`;
  }

  getLineWidth(index: number): number {
    // Return pre-computed width or fallback
    return this._lineWidths[index] ?? this.width;
  }

  getParagraphLineWidth(index: number): number {
    // Return pre-computed width or fallback
    return this._paragraphWidths[index] ?? 100;
  }
}
